// src/pages/ScheduleActivationCall.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';
import SubFooter from '../frontend/components/SubFooter';

// PrimeReact Imports
import { Calendar } from 'primereact/calendar';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const API_BASE = (process.env.REACT_APP_API_URL);
         
function ScheduleActivationCall() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  
  const [timezone, setTimezone] = useState('WAT');
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    guests: [],
    meetingNotes: '',
    phoneNumber: '',
    countryCode: '+234'
  });

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    const checkAccess = async () => {
      const step3Data = JSON.parse(localStorage.getItem('signupStep3') || '{}');
      const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
      
      if (step3Data.sessionId) {
        console.log('✅ Access granted: Signup flow detected');
        loadSignupData();
        setIsLoadingData(false);
        return;
      }
      
      if (userSession?.sessionId) {
        console.log('✅ Access granted: Login session detected');
        
        if (userSession.isSchedule === true || userSession.isSchedule === 1) {
          console.log('🔄 Activation call already scheduled - redirecting to dashboard');
          toast.info('Activation call already scheduled!');
          setTimeout(() => {
            navigate('/onboarding-dashboard', { 
              replace: true,
              state: { fromScheduleActivation: true, sessionId: userSession.sessionId }
            });
          }, 1000);
          return;
        }
        
        await loadLoginData(userSession);
        setIsLoadingData(false);
        return;
      }
      
      console.log('🔍 No valid session found, redirecting to /get-started');
      toast.warning('Please start registration or login first');
      setTimeout(() => {
        navigate('/get-started', { replace: true });
      }, 1000);
    };
    
    checkAccess();
  }, [navigate]);

  const loadSignupData = () => {
    try {
      const step3 = JSON.parse(localStorage.getItem('signupStep3') || '{}');
      const step1 = JSON.parse(localStorage.getItem('signupStep1') || '{}');
      
      setFormData(prev => ({
        ...prev,
        name: `${step1.firstName || ''} ${step1.lastName || ''}`.trim() || prev.name,
        email: step1.email || step3.email || prev.email,
        phoneNumber: step1.phone || prev.phoneNumber,
        countryCode: step1.countryCode || '+234'
      }));
    } catch (e) {
      console.warn('Failed to load signup data:', e);
    }
  };

  const loadLoginData = async (session) => {
    try {
      setIsLoadingData(true);
      
      const apiUrl = `${API_BASE}/HMO/get-company-by-session`;
      const payload = { sessionId: session.sessionId.toString() };
      
      console.log('🔍 Fetching company data for schedule activation:', payload);
      
      const result = await authenticatedFetch(apiUrl, 'POST', payload);
      
      if (result?.success === true && result?.data) {
        const data = result.data;
        
        setFormData(prev => ({
          ...prev,
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || prev.name,
          email: data.email || session.userEmail || prev.email,
          phoneNumber: data.phoneNumber?.replace(/^(\+?\d+)/, '') || data.businessPhone?.replace(/^(\+?\d+)/, '') || prev.phoneNumber,
          countryCode: data.phoneNumber?.match(/^(\+?\d+)/)?.[1] || data.businessPhone?.match(/^(\+?\d+)/)?.[1] || '+234'
        }));
        
        console.log('✅ Loaded user data from API');
      } else {
        setFormData(prev => ({
          ...prev,
          name: `${session.firstName || ''} ${session.lastName || ''}`.trim() || prev.name,
          email: session.userEmail || prev.email
        }));
        console.log('⚠️ Using session data as fallback');
      }
    } catch (error) {
      console.error('💥 Failed to load login data:', error);
      setFormData(prev => ({
        ...prev,
        name: `${session.firstName || ''} ${session.lastName || ''}`.trim() || prev.name,
        email: session.userEmail || prev.email
      }));
    } finally {
      setIsLoadingData(false);
    }
  };

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
    '04:00 PM', '04:30 PM'
  ];

  const timezones = [
    { code: 'WAT', name: 'West Africa Time', offset: '+01:00' },
    { code: 'GMT', name: 'Greenwich Mean Time', offset: '+00:00' },
    { code: 'CAT', name: 'Central Africa Time', offset: '+02:00' },
    { code: 'EAT', name: 'East Africa Time', offset: '+03:00' },
    { code: 'EST', name: 'Eastern Standard Time', offset: '-05:00' },
    { code: 'PST', name: 'Pacific Standard Time', offset: '-08:00' }
  ];

  // Disable past dates and weekends
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();
    return date < today || dayOfWeek === 0 || dayOfWeek === 6;
  };

  const handleDateSelect = (e) => {
    if (e.value) {
      setSelectedDate(e.value);
      setSelectedTime(null);
      setShowDetailsForm(false);
      setShowCalendar(true);
      setSubmitError('');
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setShowDetailsForm(true);
    setShowCalendar(false);
    setSubmitError('');
  };

  const handleEditSelection = () => {
    setShowCalendar(true);
    setShowDetailsForm(false);
    setSubmitError('');
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitError) setSubmitError('');
  };

  const handleAddGuest = () => {
    const guestEmail = prompt('Enter guest email address:');
    if (guestEmail && guestEmail.includes('@')) {
      setFormData({ ...formData, guests: [...formData.guests, guestEmail] });
    }
  };

  const handleContinue = async () => {
    if (!selectedDate || !selectedTime || !formData.name || !formData.email || !formData.phoneNumber) {
      toast.warning('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const step3 = JSON.parse(localStorage.getItem('signupStep3') || '{}');
      const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
      const sessionId = step3.sessionId || userSession.sessionId;
      
      if (!sessionId) {
        throw new Error('Session not found. Please login or verify your email first.');
      }

      const apiPayload = {
        sessionId: sessionId.toString(),
        date: selectedDate.toISOString(),
        time: selectedTime,
        timezone: timezones.find(tz => tz.code === timezone)?.code || timezone,
        attendeeName: formData.name.trim(),
        phoneNumber: `${formData.countryCode}${formData.phoneNumber.replace(/\D/g, '')}`,
        guests: formData.guests.length > 0 ? formData.guests : [],
        meetingNotes: formData.meetingNotes.trim() || ''
      };

      console.log('📤 Calling authenticatedFetch to /api/HMO/schedule-activation-call...');
      
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/schedule-activation-call`,
        'POST',
        apiPayload
      );

      console.log('✅ API Response:', result);

      if (result.success === false || result.status === 'error') {
        console.error('❌ API returned error:', result);
        const errorMessage = result.message || 'Failed to schedule activation call. Please try again.';
        setSubmitError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      console.log('🎉 Activation call scheduled successfully');
      const successMessage = result.message || 'Activation call scheduled successfully!';
      
      toast.success(successMessage);
      
      localStorage.setItem('activationCall', JSON.stringify({
        ...apiPayload,
        scheduledAt: new Date().toISOString(),
        confirmationId: result.data?.confirmationId || result.confirmationId
      }));
      
      if (userSession.sessionId) {
        localStorage.setItem('userSession', JSON.stringify({
          ...userSession,
          isSchedule: true,
          lastUpdated: new Date().toISOString()
        }));
      }
      
      setTimeout(() => {
        navigate('/onboarding-dashboard', { 
          state: { 
            welcomeMessage: `Welcome, ${formData.name}! Your activation call is scheduled.`,
            isNewUser: !userSession.sessionId,
            sessionId: sessionId,
            activationCallScheduled: true
          } 
        });
      }, 800);

    } catch (error) {
      console.error('💥 Schedule Activation Call Error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        const networkMsg = 'Cannot connect to server. Please check your internet connection.';
        setSubmitError(networkMsg);
        toast.error(networkMsg);
      } else {
        const errorMsg = error.message || 'Failed to schedule activation call. Please try again.';
        setSubmitError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      console.log('🔄 Resetting loading state');
      setIsSubmitting(false);
    }
  };

  const canSelectTime = selectedDate;
  const canContinue = selectedDate && selectedTime && formData.name && formData.email && formData.phoneNumber;

  const formatDateDisplay = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (isLoadingData) {
    return (
      <div className="schedule-activation-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading your information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-activation-page">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <a href="/" className="brand-logo">
              <img src="images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
            </a>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isSubmitting}>
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="schedule-activation-section py-5" style={{ background: '#fafafa', minHeight: '80vh' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Success Icon & Title */}
              <div className="text-center mb-5" data-aos="fade-down">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 mb-3" 
                     style={{ width: '60px', height: '60px', border: '3px solid #5a4fcf' }}>
                  <i className="fas fa-check text-success" style={{ fontSize: '30px' }}></i>
                </div>
                <h1 className="mb-3" data-aos="fade-up">Schedule your activation call</h1>
              </div>

              <div className="row g-4">
                {/* Left Panel - Information */}
                <div className="col-lg-4" data-aos="fade-right">
                  <div className="card h-100 border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="card-body p-4">
                      <h5 className="mb-3 fw-bold">Select a date</h5>
                      
                      <div className="mb-4">
                        <p className="small text-muted mb-3">
                          The <strong>Activation call</strong> is a personalized session with at least one of our team members to ease you into your account validation and starting integration.
                        </p>
                      </div>

                      <div className="mb-4">
                        <h6 className="fw-bold mb-3" style={{ color: '#5a4fcf' }}>
                          With Activation call, we are able to:
                        </h6>
                        <ul className="list-unstyled">
                          <li className="mb-3 d-flex align-items-start">
                            <i className="fas fa-check-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                            <small className="text-muted">Validate and verify your business</small>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <i className="fas fa-check-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                            <small className="text-muted">Assign you an account manager that suits your needs</small>
                          </li>
                          <li className="mb-3 d-flex align-items-start">
                            <i className="fas fa-check-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                            <small className="text-muted">Provide support by giving you a personalized experience</small>
                          </li>
                        </ul>
                      </div>

                      <div className="mt-auto pt-4 border-top">
                        <span className="d-flex align-items-center gap-2 text-muted">
                          <i className="fas fa-shield-alt" style={{ color: '#5a4fcf' }}></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Panel - Calendar & Form */}
                <div className="col-lg-8" data-aos="fade-left">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      
                      {submitError && (
                        <div className="alert alert-danger border-0 mb-4" role="alert">
                          <i className="fas fa-exclamation-circle me-2"></i>
                          {submitError}
                        </div>
                      )}

                      {/* CALENDAR SECTION - PrimeReact Calendar (INLINE / OPEN BY DEFAULT) */}
                      {showCalendar && (
                        <div data-aos="fade-in">
                          <div className="mb-4">
                            <label className="form-label fw-semibold small mb-3 d-block text-center">Select Date</label>
                            
                            {/* ✅ Inline Calendar Wrapper - Centered with Enhanced Styling */}
                            <div className="d-flex justify-content-center mb-3">
                              <div className="calendar-wrapper p-4 border rounded shadow-sm" style={{ 
                                backgroundColor: '#fff', 
                                maxWidth: '400px', 
                                width: '100%',
                                borderRadius: '12px'
                              }}>
                                <Calendar 
                                  inline
                                  value={selectedDate}
                                  onChange={handleDateSelect}
                                  disabledDate={isDateDisabled}
                                  dateFormat="yy-mm-dd"
                                  disabled={isSubmitting}
                                  className="w-100 custom-calendar"
                                />
                              </div>
                            </div>
                            
                            <small className="text-muted d-block text-center">
                              <i className="pi pi-info-circle me-1"></i>
                              Weekends and past dates are not available
                            </small>
                          </div>

                          {/* Timezone Selector */}
                          <div className="mt-4 pt-4 border-top">
                            <label className="form-label fw-semibold small">Time zone</label>
                            <select
                              className="form-select"
                              value={timezone}
                              onChange={(e) => setTimezone(e.target.value)}
                              style={{ maxWidth: '300px' }}
                              disabled={isSubmitting}
                            >
                              {timezones.map((tz) => (
                                <option key={tz.code} value={tz.code}>
                                  {tz.name} ({tz.offset})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Time Selection */}
                          {canSelectTime && (
                            <div className="mt-4" data-aos="fade-up">
                              <label className="form-label fw-semibold small mb-3 d-block">Select a time</label>
                              <div className="row g-2">
                                {timeSlots.map((time) => (
                                  <div key={time} className="col-6 col-md-4 col-lg-3">
                                    <button
                                      className={`btn w-100 py-2 rounded ${selectedTime === time ? 'text-white' : 'btn-outline-secondary'}`}
                                      style={{
                                        backgroundColor: selectedTime === time ? '#5a4fcf' : 'transparent',
                                        borderColor: selectedTime === time ? '#5a4fcf' : '#e9ecef',
                                        fontSize: '14px'
                                      }}
                                      onClick={() => handleTimeSelect(time)}
                                      disabled={isSubmitting}
                                    >
                                      {time}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* SELECTED DATE/TIME SUMMARY */}
                      {!showCalendar && selectedDate && selectedTime && (
                        <div className="mb-4 p-4 rounded" style={{ backgroundColor: '#f3f0ff', border: '2px solid #5a4fcf' }} data-aos="fade-in">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h5 className="mb-2 fw-bold" style={{ color: '#5a4fcf' }}>
                                <i className="far fa-calendar-check me-2"></i>
                                Your Scheduled Call
                              </h5>
                              <p className="mb-1"><strong>Date:</strong> {formatDateDisplay(selectedDate)}</p>
                              <p className="mb-1"><strong>Time:</strong> {selectedTime} ({timezone})</p>
                              <p className="mb-0"><strong>Timezone:</strong> {timezones.find(tz => tz.code === timezone)?.name}</p>
                            </div>
                            <button
                              className="btn btn-outline-primary"
                              onClick={handleEditSelection}
                              disabled={isSubmitting}
                              style={{ borderColor: '#5a4fcf', color: '#5a4fcf' }}
                            >
                              <i className="fas fa-edit me-2"></i>Change
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Enter Details Form */}
                      {showDetailsForm && (
                        <div className="mt-4" data-aos="fade-up">
                          <h5 className="mb-4 fw-bold">Enter Details</h5>
                          
                          <form onSubmit={(e) => e.preventDefault()}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold small">Name *</label>
                              <input
                                type="text"
                                name="name"
                                className="form-control"
                                value={formData.name}
                                onChange={handleFormChange}
                                required
                                placeholder="Your full name"
                                style={{ 
                                  backgroundColor: '#f8f9fa', 
                                  border: submitError ? '2px solid #dc3545' : '1px solid #e9ecef' 
                                }}
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label fw-semibold small">Email *</label>
                              <input
                                type="email"
                                name="email"
                                className="form-control"
                                value={formData.email}
                                onChange={handleFormChange}
                                required
                                placeholder="your.email@example.com"
                                style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                                disabled={isSubmitting}
                              />
                            </div>

                            <div className="mb-3">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleAddGuest}
                                disabled={isSubmitting}
                                style={{ borderColor: '#5a4fcf', color: '#5a4fcf' }}
                              >
                                <i className="fas fa-plus me-2"></i>Add Guests
                              </button>
                              {formData.guests.length > 0 && (
                                <div className="mt-2">
                                  {formData.guests.map((guest, idx) => (
                                    <span key={idx} className="badge bg-light text-dark me-2 mb-1">
                                      {guest}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-sm ms-2"
                                        onClick={() => {
                                          const newGuests = formData.guests.filter((_, i) => i !== idx);
                                          setFormData({ ...formData, guests: newGuests });
                                        }}
                                        disabled={isSubmitting}
                                      ></button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="mb-3">
                              <label className="form-label fw-semibold small">
                                Please share anything that will help prepare for our meeting.
                              </label>
                              <textarea
                                name="meetingNotes"
                                className="form-control"
                                rows="3"
                                value={formData.meetingNotes}
                                onChange={handleFormChange}
                                placeholder="GETTING TO KNOW THE PRODUCT"
                                style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                                disabled={isSubmitting}
                              ></textarea>
                            </div>

                            <div className="mb-3">
                              <label className="form-label fw-semibold small">WhatsApp or Phone number *</label>
                              <div className="input-group">
                                <select 
                                  name="countryCode"
                                  className="form-select"
                                  value={formData.countryCode}
                                  onChange={handleFormChange}
                                  style={{ 
                                    maxWidth: '100px', 
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRight: 'none',
                                    borderTopRightRadius: '0',
                                    borderBottomRightRadius: '0'
                                  }}
                                  disabled={isSubmitting}
                                >
                                  <option value="+234">🇳🇬 +234</option>
                                  <option value="+1">🇺 +1</option>
                                  <option value="+44">🇬 +44</option>
                                  <option value="+233">🇬🇭 +233</option>
                                  <option value="+254">🇰🇪 +254</option>
                                  <option value="+27">🇿🇦 +27</option>
                                </select>
                                <input
                                  type="tel"
                                  name="phoneNumber"
                                  className="form-control"
                                  value={formData.phoneNumber}
                                  onChange={handleFormChange}
                                  required
                                  placeholder="7031234567"
                                  style={{ 
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderTopLeftRadius: '0',
                                    borderBottomLeftRadius: '0'
                                  }}
                                  disabled={isSubmitting}
                                />
                              </div>
                            </div>

                            <div className="mb-4">
                              <small className="text-muted">
                                By proceeding, you confirm that you have read and agree to Calendly's{' '}
                                <a href="/terms" className="text-decoration-none" style={{ color: '#5a4fcf' }}>Terms of Use</a>
                                {' '}and{' '}
                                <a href="/privacy" className="text-decoration-none" style={{ color: '#5a4fcf' }}>Privacy Notice</a>.
                              </small>
                            </div>

                            <div className="d-grid">
                              <button
                                type="button"
                                className={`btn btn-lg py-3 ${canContinue && !isSubmitting ? 'text-white' : 'btn-secondary'}`}
                                onClick={handleContinue}
                                disabled={!canContinue || isSubmitting}
                                style={{
                                  backgroundColor: canContinue && !isSubmitting ? '#5a4fcf' : '#6c757d',
                                  border: 'none',
                                  fontWeight: '600',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {isSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }}></span>
                                    Scheduling...
                                  </>
                                ) : (
                                  <>
                                    Schedule Event <i className="fas fa-arrow-right ms-2"></i>
                                  </>
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* Cookie Settings */}
                     
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SubFooter/>
      
      {/* Custom CSS for Calendar Styling */}
      <style>{`
        .calendar-wrapper {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        
        .custom-calendar .p-datepicker {
          border: none;
          padding: 0;
        }
        
        .custom-calendar .p-datepicker-header {
          background: linear-gradient(135deg, #5a4fcf 0%, #6c63ff 100%);
          color: white;
          border: none;
          border-radius: 8px 8px 0 0;
          padding: 12px;
          font-weight: 600;
        }
        
        .custom-calendar .p-datepicker-header .p-datepicker-title {
          font-size: 1.1rem;
          font-weight: 600;
        }
        
        .custom-calendar .p-datepicker-header button {
          color: white;
          transition: all 0.3s ease;
        }
        
        .custom-calendar .p-datepicker-header button:hover {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
        }
        
        .custom-calendar .p-datepicker table {
          font-size: 0.95rem;
          margin-top: 8px;
        }
        
        .custom-calendar .p-datepicker table td > span {
          width: 40px;
          height: 40px;
          line-height: 40px;
          border-radius: 50%;
          transition: all 0.3s ease;
          font-weight: 500;
        }
        
        .custom-calendar .p-datepicker table td > span:hover {
          background: #f3f0ff;
          color: #5a4fcf;
        }
        
        .custom-calendar .p-datepicker table td.p-datepicker-today > span {
          background: #5a4fcf !important;
          color: white !important;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(90, 79, 207, 0.3);
        }
        
        .custom-calendar .p-datepicker table td.p-datepicker-today > span:hover {
          background: #4a3fbf !important;
        }
        
        .custom-calendar .p-datepicker table td.p-datepicker-selected > span {
          background: #5a4fcf;
          color: white;
        }
        
        .custom-calendar .p-datepicker table td.p-datepicker-other-month > span {
          color: #c0c0c0;
          opacity: 0.6;
        }
        
        .custom-calendar .p-datepicker table td.p-datepicker-disabled > span {
          color: #d0d0d0;
          cursor: not-allowed;
          text-decoration: line-through;
        }
        
        .custom-calendar .p-datepicker-weekday {
          padding: 12px 0;
          font-weight: 700;
          color: #5a4fcf;
          text-transform: uppercase;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
        }
      `}</style>
    </div>
  );
}

export default ScheduleActivationCall;